import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Wifi,
  LayoutDashboard,
  FileText,
  CreditCard,
  TicketIcon,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "./NotificationBell";
import { useCMS } from "../context/CMSContext";

export default function ClientLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const { settings } = useCMS();
  const companyName = settings?.company_name || "ISP Manager";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/client/login");
  };

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/client/dashboard" },
    { icon: FileText, label: "SOA History", path: "/client/soa" },
    { icon: CreditCard, label: "Payments", path: "/client/payments" },
    { icon: TicketIcon, label: "Support Tickets", path: "/client/tickets" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Wifi className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-slate-900">{companyName}</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        {/* <aside
          className={`
          fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-white border-r border-slate-200
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        > */}

        <aside
          className={`
          fixed lg:sticky top-0 left-0 z-50 h-dvh overflow-y-auto w-64 bg-white border-r border-slate-200
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-6 border-b border-slate-100 hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Wifi className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-slate-900">{companyName}</h1>
                  <p className="text-xs text-slate-500">Client Portal</p>
                </div>
              </div>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-slate-100">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm font-semibold text-blue-900">
                  {user.full_name || "Client"}
                </p>
                <p className="text-xs text-blue-700">
                  {user.account_number || user.email}
                </p>
              </div>
            </div>

            {/* Navigation */}
            {/* <nav className="flex-1 p-4 space-y-1"> */}
            <nav className={`flex-1 min-h-0 p-4 space-y-1 overflow-y-auto`}>
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                    ${
                      isActive(item.path)
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }
                  `}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Logout */}
            <div className="p-4 border-t border-slate-100">
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
                onClick={handleLogout}
                data-testid="client-logout-btn"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/20 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          {/* Desktop Notification Bell */}
          <div className="hidden lg:flex justify-end px-6 py-3 border-b border-slate-100 bg-white">
            <NotificationBell />
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}
